import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CarSpec } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { formatLicensePlate } from 'noddi-util';
import { VehicleCategoryIcon, useAuthContext } from 'noddi-web';
import { useUserGroupSelection } from '../../../../hooks/useUserGroupSelection';
import { useWheelStorageOwnerGroup } from '../../../../hooks/useWheelStorageOwnerGroup';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { CustomerSMSLogin } from '../../../Login/CustomerSMSLogin';

interface Props {
  open: boolean;
  onClose: () => void;
  car: CarSpec | null;
  addCar: (car: CarSpec) => void;
}

export const WheelStorageContractDialog = ({ open, onClose, car, addCar }: Props) => {
  const { isLoggedIn, updateUserData } = useAuthContext();
  const { selectUserGroup } = useUserGroupSelection();
  const { brandEmail: email } = useCurrentBrand();
  const getWheelStorageOwnerGroup = useWheelStorageOwnerGroup();

  if (!car) {
    return null;
  }

  // Without an owning user group the viewer cant do any action on this car
  const userGroupToSwitchTo = getWheelStorageOwnerGroup(car);
  const hasAccess = !!userGroupToSwitchTo;

  const handleSwitchToUser = () => {
    if (hasAccess) {
      selectUserGroup(userGroupToSwitchTo.id, { keepBookingDrafts: true });
      addCar(car);
      onClose();
    }
  };

  return (
    <NoddiDialog
      open={open}
      onClose={onClose}
      title={isLoggedIn ? t`The car has a tire hotel contract with another user` : t`Sign in`}
      description={
        isLoggedIn
          ? undefined
          : t`This car is registered with a tire hotel contract. Please sign in to the account associated with the contract to continue.`
      }
      footer={
        isLoggedIn ? (
          // No need to show buttons if not logged in, the login component have its own buttons
          hasAccess ? (
            <div className='flex gap-3'>
              <NoddiButton variant='outline' onClick={onClose} className='flex-1'>
                <Trans>Cancel</Trans>
              </NoddiButton>
              <NoddiButton onClick={handleSwitchToUser} className='flex-1'>
                <Trans>Switch to</Trans> {userGroupToSwitchTo.name}
              </NoddiButton>
            </div>
          ) : (
            <div className='flex justify-end'>
              <NoddiButton variant='outline' onClick={onClose}>
                <Trans>Close</Trans>
              </NoddiButton>
            </div>
          )
        ) : undefined
      }
    >
      <div className='flex flex-col gap-5'>
        <div className='flex items-start gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4'>
          <div className='flex size-12 shrink-0 items-center justify-center rounded-full bg-neutral-200'>
            <VehicleCategoryIcon vehicleCategory={car.vehicleCategory} size='medium' />
          </div>
          <div className='flex-1'>
            <p className='font-semibold text-gray-900'>
              {car.make} {car.model}
            </p>
            <p className='text-sm text-gray-600'>{formatLicensePlate(car.licensePlate.number)}</p>
          </div>
        </div>

        {isLoggedIn ? (
          // If u are already logged in, show info about switching user groups if other user group you have access to has access to the car
          <>
            {userGroupToSwitchTo ? (
              <div className='flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4'>
                <div className='flex size-12 shrink-0 items-center justify-center rounded-full bg-neutral-200'>
                  <NoddiIcon name='User' size='medium' />
                </div>
                <div className='flex-1'>
                  <p className='font-semibold text-gray-900'>{userGroupToSwitchTo.name}</p>
                  <p className='mt-1 text-sm text-gray-600'>
                    <Trans>Switch to this user to continue with this car.</Trans>
                  </p>
                </div>
              </div>
            ) : (
              <div className='flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4'>
                <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-red-100'>
                  <NoddiIcon name='TriangleAlert' className='text-red-600' size='medium' />
                </div>
                <div className='flex-1'>
                  <p className='font-semibold text-red-900'>
                    <Trans>Access restricted</Trans>
                  </p>
                  <p className='mt-1 text-sm text-red-700'>
                    <Trans>
                      This car is not available in your account. Please contact support at {email} if this is an error.
                    </Trans>
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          // If user logs in and has access to the user group
          // We automatically sign the user into the correct user group on login, works better than showing the user group selection inline here.
          <CustomerSMSLogin
            onLoginSuccess={(userData) => {
              const userToChangeTo = car.wheelStorage?.[0]?.userGroupId;
              const hasAccess = userData.user.userGroups.some((ug) => ug.id === userToChangeTo);
              // We cannot reuse the selectUserGroup hook here, as we need fresh userData from the login, which is not yet set in the context
              const newUserData = {
                ...userData.user,
                userGroups: userData.user.userGroups.map((ug) => ({
                  ...ug,
                  isSelected: ug.id === userToChangeTo
                }))
              };
              if (hasAccess) {
                // no need to check for if they are impersonating or not here, as they have just logged inn.
                updateUserData({ ...userData, user: newUserData });
                addCar(car);
                onClose();
              }
            }}
          />
        )}
      </div>
    </NoddiDialog>
  );
};
